import {useEffect, useState} from 'react'
import {
  adminGetAccountNumbers,
  adminGetCampaigns,
  adminSaveCampaignSignature,
  adminGetProfiles,
  adminSaveProfileSignature,
  adminAssignProfiles,
  adminGetPosts,
  adminSuggestStrip,
  adminBulkUpdatePosts,
  adminSuggestAddContact,
  adminRemoveVirtualNumber,
  adminGenerateSignature,
  adminConsolidateReplies,
  adminGetReplyBackup,
  adminBulkEditReplies,
  adminApplyReplyRows,
  adminRestoreReplies,
  AccountSearchResult,
  DidOption,
  CampaignSig,
  ProfileSig,
  StripPost,
  ReplyBulkEditSample,
} from './_requests'

const FB_TOKEN = process.env.REACT_APP_FB_ACCESS_TOKEN

function accLabel(a: AccountSearchResult): string {
  const name = [a.first_name, a.last_name].filter(Boolean).join(' ').trim()
  return name || a.company_name || a.email || a.phone || a._id
}

function Avatar({facebookId, src, name}: {facebookId?: string; src?: string; name: string}) {
  const [failed, setFailed] = useState(false)
  const graph = facebookId && FB_TOKEN ? `https://graph.facebook.com/${facebookId}/picture?type=square&access_token=${FB_TOKEN}` : ''
  const url = graph || src
  return url && !failed ? (
    <img src={url} alt='' onError={() => setFailed(true)} style={{width: 32, height: 32, borderRadius: '50%', objectFit: 'cover', flexShrink: 0}} />
  ) : (
    <span
      className='d-inline-flex align-items-center justify-content-center'
      style={{width: 32, height: 32, borderRadius: '50%', background: '#e4e6ef', fontSize: 13, flexShrink: 0}}
    >
      {(name || '?').trim().charAt(0)}
    </span>
  )
}

function AiSignatureButton({context, campaignId, onGenerated}: {context?: string; campaignId?: string; onGenerated: (sig: string) => void}) {
  const [busy, setBusy] = useState(false)
  const [err, setErr] = useState('')
  const gen = async () => {
    setBusy(true)
    setErr('')
    try {
      const res = await adminGenerateSignature(context, campaignId)
      if (res.data?.signature) onGenerated(res.data.signature)
      else setErr('לא התקבלה חתימה')
    } catch (e: any) {
      const m = e?.response?.data?.error || ''
      setErr(/credit balance/i.test(m) ? 'אין יתרת קרדיט ב-AI' : m || 'שגיאה ביצירת חתימה')
    } finally {
      setBusy(false)
    }
  }
  return (
    <>
      <button type='button' className='btn btn-sm btn-light-primary' onClick={gen} disabled={busy}>
        {busy ? 'יוצר…' : '✨ צור עם AI'}
      </button>
      {err && <span className='text-danger fs-8'>{err}</span>}
    </>
  )
}

// One campaign's signature + lock (admin can always edit + toggle).
function CampaignCard({c}: {c: CampaignSig}) {
  const [sig, setSig] = useState(c.default_signature)
  const [saved, setSaved] = useState(c.default_signature)
  const [locked, setLocked] = useState(c.default_signature_locked)
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')
  const save = async () => {
    setSaving(true)
    setMsg('')
    try {
      const res = await adminSaveCampaignSignature(c._id, {default_signature: sig, default_signature_locked: locked})
      const camp = res.data.campaign
      setSig(camp.default_signature)
      setSaved(camp.default_signature)
      setLocked(camp.default_signature_locked)
      setMsg('נשמר ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה')
    } finally {
      setSaving(false)
    }
  }
  return (
    <div className='border rounded p-4 mb-3'>
      <div className='d-flex justify-content-between align-items-center mb-2'>
        <span className='fw-bold'>{c.name}</span>
        <label className='d-flex align-items-center gap-2 m-0' style={{cursor: 'pointer'}}>
          <input type='checkbox' checked={locked} onChange={(e) => setLocked(e.target.checked)} style={{width: 16, height: 16, margin: 0}} />
          <span className='fs-8'>🔒 נעל ללקוח</span>
        </label>
      </div>
      <textarea
        className='form-control form-control-sm'
        rows={3}
        dir='rtl'
        placeholder={'לדוגמה: 📞 לפרטים: {number}\n💬 וואטסאפ: {wa_link}'}
        value={sig}
        onChange={(e) => setSig(e.target.value)}
      />
      <div className='d-flex gap-2 align-items-center mt-2 flex-wrap'>
        <button className='btn btn-sm btn-primary' onClick={save} disabled={saving || (sig === saved && locked === c.default_signature_locked)}>
          {saving ? 'שומר…' : 'שמור'}
        </button>
        <AiSignatureButton context={c.name} campaignId={c._id} onGenerated={setSig} />
        {msg && <span className='text-muted fs-8'>{msg}</span>}
      </div>
    </div>
  )
}

// One profile: avatar, DID assignment, and an optional per-profile signature
// (override checkbox — off means it uses the campaign signature).
function ProfileCard({p, numbers, accountId}: {p: ProfileSig; numbers: DidOption[]; accountId: string}) {
  const [override, setOverride] = useState(!!(p.default_signature || '').trim())
  const [sig, setSig] = useState(p.default_signature)
  const [saved, setSaved] = useState(p.default_signature)
  const [locked, setLocked] = useState(p.default_signature_locked)
  const [didId, setDidId] = useState(p.virtual_number_id || '')
  const [saving, setSaving] = useState(false)
  const [msg, setMsg] = useState('')

  const persist = async (sigValue: string) => {
    setSaving(true)
    setMsg('')
    try {
      const res = await adminSaveProfileSignature(p._id, {default_signature: sigValue, default_signature_locked: locked})
      setSig(res.data.profile.default_signature)
      setSaved(res.data.profile.default_signature)
      setLocked(res.data.profile.default_signature_locked)
      setMsg('נשמר ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה')
    } finally {
      setSaving(false)
    }
  }
  const toggleOverride = (checked: boolean) => {
    setOverride(checked)
    if (!checked) {
      setSig('')
      if (saved.trim()) persist('')
    }
  }
  const assignDid = async (value: string) => {
    setDidId(value)
    setMsg('')
    try {
      await adminAssignProfiles(accountId, {virtual_number_id: value || null, profile_ids: [p._id]})
      setMsg('המספר עודכן ✓')
    } catch (e: any) {
      setMsg(e?.response?.data?.error || 'שגיאה בהקצאת מספר')
    }
  }
  return (
    <div className='border rounded p-4 mb-3'>
      <div className='d-flex justify-content-between align-items-center mb-2 flex-wrap gap-2'>
        <div className='d-flex align-items-center gap-2'>
          <Avatar facebookId={p.facebook_id} src={p.profile_image} name={p.name} />
          <span className='fw-bold'>{p.name}</span>
        </div>
        <div className='d-flex align-items-center gap-2'>
          <select className='form-select form-select-sm' style={{width: 210}} value={didId} onChange={(e) => assignDid(e.target.value)}>
            <option value=''>ברירת מחדל (מספר החשבון)</option>
            {numbers.map((n) => (
              <option key={n._id} value={n._id}>{n.ddi}</option>
            ))}
          </select>
          <label className='d-flex align-items-center gap-1 m-0' style={{cursor: 'pointer'}}>
            <input type='checkbox' checked={locked} onChange={(e) => setLocked(e.target.checked)} style={{width: 16, height: 16, margin: 0}} />
            <span className='fs-8'>🔒</span>
          </label>
        </div>
      </div>
      <label className='d-flex align-items-center gap-2 mb-2' style={{cursor: 'pointer'}}>
        <input type='checkbox' checked={override} onChange={(e) => toggleOverride(e.target.checked)} style={{width: 16, height: 16, flexShrink: 0, margin: 0}} />
        <span className='fs-7 fw-semibold'>חתימה נפרדת לפרופיל זה</span>
        <span className='text-muted fs-8'>— גוברת על חתימת הקמפיין</span>
      </label>
      {override ? (
        <>
          <textarea
            className='form-control form-control-sm'
            rows={2}
            dir='rtl'
            placeholder={'חתימה לפרופיל זה'}
            value={sig}
            onChange={(e) => setSig(e.target.value)}
          />
          <div className='d-flex gap-2 align-items-center mt-2 flex-wrap'>
            <button className='btn btn-sm btn-primary' onClick={() => persist(sig)} disabled={saving || sig === saved}>
              {saving ? 'שומר…' : 'שמור חתימה'}
            </button>
            <AiSignatureButton context={p.name} onGenerated={setSig} />
            {msg && <span className='text-muted fs-8'>{msg}</span>}
          </div>
        </>
      ) : (
        <div className='text-muted fs-8'>משתמש בחתימת הקמפיין{msg ? ` · ${msg}` : ''}</div>
      )}
    </div>
  )
}

interface StripRow extends StripPost {
  suggested: string
  include: boolean
}

export interface SetupAccount {
  _id: string
  first_name?: string
  last_name?: string
  company_name?: string
  email?: string
  phone?: string
}

// The setup popup, opened for a specific account (from the numbers table).
export function SignatureSetupModal({account, onClose}: {account: SetupAccount; onClose: () => void}) {
  const [tab, setTab] = useState<'signatures' | 'strip'>('signatures')
  const [numbers, setNumbers] = useState<DidOption[]>([])
  const [campaigns, setCampaigns] = useState<CampaignSig[]>([])
  const [profiles, setProfiles] = useState<ProfileSig[]>([])
  const [loadErr, setLoadErr] = useState('')

  const [bulkDid, setBulkDid] = useState('')
  const [bulkMsg, setBulkMsg] = useState('')

  const [posts, setPosts] = useState<StripRow[]>([])
  const [postsLoaded, setPostsLoaded] = useState(false)
  const [postsLoading, setPostsLoading] = useState(false)
  const [suggesting, setSuggesting] = useState(false)
  const [savingPosts, setSavingPosts] = useState(false)
  const [stripMsg, setStripMsg] = useState('')
  // Live progress for the batched AI rewrite (clean / re-add), so a large account
  // doesn't just sit on "מנקה…" for a minute with no feedback.
  const [progress, setProgress] = useState<{done: number; total: number} | null>(null)
  // Campaign-global auto-reply: replaces per-post reply messages with one reply
  // that carries the virtual-number contact details via {number}/{wa_link}.
  const [globalReply, setGlobalReply] = useState('שלום 🙂 תודה על ההתעניינות!\nלפרטים נוספים חייגו: {number}\nאו בוואטסאפ: {wa_link}')
  const [consolidating, setConsolidating] = useState(false)
  const [replyMsg, setReplyMsg] = useState('')
  // Undo of the consolidation: how many per-post replies still have a pre-clean
  // backup, so the admin can put the customer's real numbers back.
  const [replyBackup, setReplyBackup] = useState<{posts: number; campaigns: number; backed_up_at: string | null; sample: string} | null>(null)
  const [restoring, setRestoring] = useState(false)
  // Bulk add/remove/replace of a phone number across every post's reply text.
  // Always previewed before it writes — this rewrites thousands of replies.
  const [editMode, setEditMode] = useState<'remove' | 'replace' | 'add'>('replace')
  const [editFrom, setEditFrom] = useState('')
  const [editTo, setEditTo] = useState('')
  const [editText, setEditText] = useState('')
  const [editPosition, setEditPosition] = useState<'start' | 'end'>('end')
  const [editDefaults, setEditDefaults] = useState(true)
  const [editBusy, setEditBusy] = useState(false)
  const [editMsg, setEditMsg] = useState('')
  const [editPreview, setEditPreview] = useState<{changed: number; scanned: number; campaigns: number; truncated: boolean} | null>(null)
  // Every rewritten reply, editable in place before it's written.
  const [editRows, setEditRows] = useState<(ReplyBulkEditSample & {include: boolean})[]>([])

  // Reverse flow: re-insert the user's real phone into the posts and tear down
  // the virtual-number setup (clear signatures + auto-replies).
  const [reversePhone, setReversePhone] = useState('')
  const [reversing, setReversing] = useState(false)
  const [removing, setRemoving] = useState(false)

  useEffect(() => {
    ;(async () => {
      try {
        const [nRes, cRes, pRes] = await Promise.all([
          adminGetAccountNumbers(account._id),
          adminGetCampaigns(account._id),
          adminGetProfiles(account._id),
        ])
        setNumbers(nRes.data.numbers || [])
        setCampaigns(cRes.data.campaigns || [])
        setProfiles(pRes.data.profiles || [])
      } catch (e: any) {
        setLoadErr(e?.response?.data?.error || 'שגיאה בטעינת נתוני החשבון')
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [account._id])

  const assignAll = async () => {
    if (!account) return
    setBulkMsg('')
    try {
      const res = await adminAssignProfiles(account._id, {virtual_number_id: bulkDid || null, all: true})
      setProfiles((prev) => prev.map((x) => ({...x, virtual_number_id: bulkDid})))
      setBulkMsg(`עודכנו ${res.data.modified} פרופילים`)
    } catch (e: any) {
      setBulkMsg(e?.response?.data?.error || 'שגיאה')
    }
  }

  const loadAllPosts = async () => {
    if (!account) return
    setPostsLoading(true)
    setStripMsg('')
    try {
      const res = await adminGetPosts(account._id)
      setPosts((res.data.posts || []).map((p) => ({...p, suggested: '', include: true})))
      setPostsLoaded(true)
    } catch (e: any) {
      setStripMsg(e?.response?.data?.error || 'שגיאה בטעינת המודעות')
    } finally {
      setPostsLoading(false)
    }
  }

  const openStrip = () => {
    setTab('strip')
    if (!postsLoaded && !postsLoading) loadAllPosts()
    if (!replyBackup) loadReplyBackup()
  }

  // Run the AI rewrite over all loaded posts in small parallel batches, updating
  // rows + progress as each batch lands. Splitting into many short HTTP requests
  // (instead of one long one over all 500 posts) keeps each request well under the
  // proxy timeout and finishes faster (several batches in flight at once).
  const runBatchedSuggest = async (
    call: (ids: string[]) => Promise<{data: {suggestions: {id: string; suggested: string | null; error?: string | null}[]}}>
  ): Promise<{filled: number; errored: number; total: number; sampleError: string}> => {
    const ids = posts.map((p) => p.id)
    const CHUNK = 20
    const PARALLEL = 3
    const chunks: string[][] = []
    for (let i = 0; i < ids.length; i += CHUNK) chunks.push(ids.slice(i, i + CHUNK))
    let done = 0
    let filled = 0
    let errored = 0
    let sampleError = ''
    let cursor = 0
    setProgress({done: 0, total: ids.length})
    const worker = async () => {
      while (cursor < chunks.length) {
        const chunk = chunks[cursor++]
        try {
          const res = await call(chunk)
          const byId: Record<string, string> = {}
          ;(res.data.suggestions || []).forEach((s) => {
            // A 200 can still carry per-post failures (suggested:null + error),
            // e.g. the AI provider is out of credits — surface, don't swallow.
            if (s.suggested != null) byId[s.id] = s.suggested
            else {
              errored++
              if (!sampleError && s.error) sampleError = s.error
            }
          })
          if (Object.keys(byId).length) {
            filled += Object.keys(byId).length
            setPosts((prev) => prev.map((p) => (byId[p.id] != null ? {...p, suggested: byId[p.id]} : p)))
          }
        } catch (e: any) {
          errored += chunk.length
          if (!sampleError) sampleError = e?.response?.data?.error || e?.message || 'request failed'
        } finally {
          done += chunk.length
          setProgress({done, total: ids.length})
        }
      }
    }
    await Promise.all(Array.from({length: Math.min(PARALLEL, chunks.length)}, () => worker()))
    return {filled, errored, total: ids.length, sampleError}
  }

  // Turn a raw provider/API error into a short, actionable Hebrew line.
  const friendlyAiError = (raw: string): string => {
    if (/credit balance is too low|Plans & Billing|insufficient_quota|billing/i.test(raw))
      return 'נגמר האשראי ב-Anthropic API — יש לטעון קרדיט בחשבון ולנסות שוב'
    if (/rate limit|429/i.test(raw)) return 'הגבלת קצב מ-Anthropic — נסה שוב בעוד רגע'
    return raw.length > 160 ? raw.slice(0, 160) + '…' : raw
  }

  const suggestAll = async () => {
    if (!account || !posts.length) return
    setSuggesting(true)
    setStripMsg('מנקה פרטי קשר עם AI…')
    try {
      const {filled, errored, total, sampleError} = await runBatchedSuggest((ids) => adminSuggestStrip(account._id, {post_ids: ids}))
      if (filled === 0) {
        setStripMsg(`הניקוי נכשל — לא עודכנה אף מודעה. ${friendlyAiError(sampleError)}`)
      } else if (errored) {
        setStripMsg(`נוקו ${filled}/${total} מודעות (${errored} נכשלו: ${friendlyAiError(sampleError)}) — בדוק, ערוך ושמור`)
      } else {
        setStripMsg('הפרטים נוקו — בדוק, ערוך ושמור')
      }
    } catch (e: any) {
      setStripMsg(e?.response?.data?.error || 'שגיאה בניקוי')
    } finally {
      setSuggesting(false)
      setProgress(null)
    }
  }

  const savePosts = async () => {
    if (!account) return
    const updates = posts
      .filter((p) => p.include && p.suggested && p.suggested !== p.post_content)
      .map((p) => ({post_id: p.id, post_content: p.suggested}))
    if (!updates.length) {
      setStripMsg('אין שינויים לשמירה')
      return
    }
    setSavingPosts(true)
    try {
      const res = await adminBulkUpdatePosts(account._id, updates)
      setStripMsg(`נשמרו ${res.data.saved} מודעות (הגרסה הקודמת בהיסטוריה)`)
    } catch (e: any) {
      setStripMsg(e?.response?.data?.error || 'שגיאה בשמירה')
    } finally {
      setSavingPosts(false)
    }
  }

  const editPayload = () => ({
    mode: editMode,
    from: editFrom.trim() || undefined,
    to: editTo.trim() || undefined,
    text: editText.trim() || undefined,
    position: editPosition,
    include_campaign_defaults: editDefaults,
  })

  // Preview always runs dry: it brings back every rewritten reply in full so the
  // admin can read, uncheck and hand-edit them. Applying then sends those exact
  // rows back, so what was approved on screen is what lands.
  const previewBulkEdit = async (override?: Partial<ReturnType<typeof editPayload>>) => {
    if (!account) return
    setEditBusy(true)
    setEditMsg('בודק…')
    try {
      // `override` lets a one-click preset preview immediately — reading the
      // state it just set would still see the old values this tick.
      const res = await adminBulkEditReplies(account._id, {...editPayload(), ...(override || {}), dry_run: true})
      const {changed, scanned, campaigns_changed, samples, truncated} = res.data
      setEditRows((samples || []).map((sm) => ({...sm, include: true})))
      setEditPreview({changed, scanned, campaigns: campaigns_changed, truncated})
      setEditMsg(changed || campaigns_changed
        ? `ישתנו ${changed} תגובות פר-פוסט${campaigns_changed ? ` ו-${campaigns_changed} תגובות קמפיין` : ''} (מתוך ${scanned} שנסרקו)`
        : `אין מה לשנות — נסרקו ${scanned} תגובות ואף אחת לא מתאימה`)
    } catch (e: any) {
      setEditPreview(null)
      setEditRows([])
      setEditMsg(e?.response?.data?.error || 'שגיאה בעריכת התגובות')
    } finally {
      setEditBusy(false)
    }
  }

  // The common job: take a customer off the virtual number and back onto their
  // own. Fills the form in and previews in one click, so it can't be got wrong.
  const useRealNumber = () => {
    const phone = (account.phone || '').trim()
    if (!phone) {
      setEditMsg('לחשבון לא שמור מספר טלפון — הזינו אותו ידנית בשדה "מספר חדש"')
      return
    }
    setEditMode('replace')
    setEditFrom('')
    setEditTo(phone)
    setEditDefaults(true)
    previewBulkEdit({mode: 'replace', from: undefined, to: phone, include_campaign_defaults: true})
  }

  const applyBulkEdit = async () => {
    if (!account) return
    const chosen = editRows.filter((r) => r.include)
    if (!chosen.length) {
      setEditMsg('לא נבחרה אף תגובה')
      return
    }
    if (!window.confirm(`לעדכן ${chosen.length} תגובות? הגרסה הנוכחית תגובה וניתן יהיה לשחזר.`)) return
    setEditBusy(true)
    setEditMsg('מחיל…')
    try {
      const res = await adminApplyReplyRows(account._id, chosen.map((r) => ({id: r.id, kind: r.kind, reply_text: r.after})))
      const {changed, campaigns_changed, skipped, backed_up} = res.data
      setEditPreview(null)
      setEditRows([])
      setEditMsg(`עודכנו ${changed} תגובות פר-פוסט${campaigns_changed ? ` ו-${campaigns_changed} תגובות קמפיין` : ''}${skipped ? ` · ${skipped} דולגו` : ''} · גובו ${backed_up} לשחזור`)
      loadReplyBackup()
    } catch (e: any) {
      setEditMsg(e?.response?.data?.error || 'שגיאה בשמירת התגובות')
    } finally {
      setEditBusy(false)
    }
  }

  const loadReplyBackup = async () => {
    try {
      const res = await adminGetReplyBackup(account._id)
      setReplyBackup({posts: res.data.posts, campaigns: res.data.campaigns, backed_up_at: res.data.backed_up_at, sample: res.data.sample})
    } catch {
      setReplyBackup(null)
    }
  }

  // Put the original per-post replies back. Backups survive the restore, so this
  // is repeatable and a later re-consolidation is still undoable.
  const restoreReplies = async () => {
    if (!account) return
    if (!window.confirm('להחזיר את הודעות התגובה המקוריות (עם המספרים הישנים) לכל הפוסטים ולשחזר את התגובה הגלובלית הקודמת?')) return
    setRestoring(true)
    setReplyMsg('משחזר…')
    try {
      const res = await adminRestoreReplies(account._id)
      setReplyMsg(`שוחזרו ${res.data.posts_restored} תגובות פר-פוסט ו-${res.data.campaigns_restored} תגובות קמפיין`)
      loadReplyBackup()
    } catch (e: any) {
      setReplyMsg(e?.response?.data?.error || 'שגיאה בשחזור התגובות')
    } finally {
      setRestoring(false)
    }
  }

  // Clear per-post reply messages and set one campaign-global auto-reply.
  const consolidateReplies = async () => {
    if (!account) return
    if (!globalReply.trim()) { setReplyMsg('יש להזין תגובה גלובלית'); return }
    if (!window.confirm('פעולה זו תמחק את הודעות התגובה של כל הפוסטים ותגדיר תגובה גלובלית אחת לכל הקמפיינים. להמשיך?')) return
    setConsolidating(true)
    setReplyMsg('מעדכן…')
    try {
      const res = await adminConsolidateReplies(account._id, {global_reply: globalReply.trim()})
      setReplyMsg(`עודכן: ${res.data.campaigns_updated} קמפיינים, נוקו ${res.data.posts_cleared} תגובות פר-פוסט (גובו ${res.data.backed_up} — ניתן לשחזור)`)
      loadReplyBackup()
    } catch (e: any) {
      setReplyMsg(e?.response?.data?.error || 'שגיאה בעדכון התגובות')
    } finally {
      setConsolidating(false)
    }
  }

  // Reverse of suggestAll: AI re-inserts the typed phone number into each post.
  const suggestAddAll = async () => {
    if (!account) return
    const phone = reversePhone.trim()
    if (!phone) {
      setStripMsg('יש להזין מספר טלפון להוספה')
      return
    }
    setReversing(true)
    setStripMsg('מוסיף את מספר הטלפון למודעות עם AI…')
    try {
      const {filled, errored, total, sampleError} = await runBatchedSuggest((ids) => adminSuggestAddContact(account._id, {phone, post_ids: ids}))
      if (filled === 0) {
        setStripMsg(`הוספת המספר נכשלה — לא עודכנה אף מודעה. ${friendlyAiError(sampleError)}`)
      } else if (errored) {
        setStripMsg(`נוסף המספר ל-${filled}/${total} מודעות (${errored} נכשלו: ${friendlyAiError(sampleError)}) — בדוק, ערוך, ואז שמור והסר מספר וירטואלי`)
      } else {
        setStripMsg('המספר נוסף — בדוק, ערוך, ואז שמור והסר מספר וירטואלי')
      }
    } catch (e: any) {
      setStripMsg(e?.response?.data?.error || 'שגיאה בהוספת המספר')
    } finally {
      setReversing(false)
      setProgress(null)
    }
  }

  // Save the reverse edits AND tear down the virtual-number setup.
  const saveReverseAndRemove = async () => {
    if (!account) return
    if (!window.confirm('לשמור את המודעות עם מספר הטלפון ולהסיר את המספר הווירטואלי? פעולה זו תנקה את החתימות והתגובות האוטומטיות בכל הקמפיינים והפרופילים בחשבון.')) return
    const updates = posts
      .filter((p) => p.include && p.suggested && p.suggested !== p.post_content)
      .map((p) => ({post_id: p.id, post_content: p.suggested}))
    setRemoving(true)
    try {
      let saved = 0
      if (updates.length) {
        const res = await adminBulkUpdatePosts(account._id, updates)
        saved = res.data.saved
      }
      const rm = await adminRemoveVirtualNumber(account._id)
      setStripMsg(`נשמרו ${saved} מודעות · נוקו ${rm.data.campaigns_cleared} קמפיינים ו-${rm.data.profiles_cleared} פרופילים (המספר הווירטואלי הוסר)`)
    } catch (e: any) {
      setStripMsg(e?.response?.data?.error || 'שגיאה בהסרת המספר הווירטואלי')
    } finally {
      setRemoving(false)
    }
  }

  const accountDdi = numbers[0]?.ddi || ''
  // Preview of the plain wa.me link the reverse rewrite will bake in (mirrors the
  // server's toPlainWaLink), so the admin sees what replaces the tracked link.
  const plainWaLink = (() => {
    let d = reversePhone.replace(/\D/g, '')
    if (d.startsWith('972')) d = d.slice(3)
    else if (d.startsWith('0')) d = d.slice(1)
    return d ? `https://wa.me/972${d}` : ''
  })()

  return (
        <div
          onClick={onClose}
          style={{
            position: 'fixed',
            inset: 0,
            background: 'rgba(0,0,0,0.5)',
            zIndex: 1090,
            display: 'flex',
            alignItems: 'flex-start',
            justifyContent: 'center',
            padding: 20,
            overflowY: 'auto',
          }}
        >
          <div onClick={(e) => e.stopPropagation()} className='card' dir='rtl' style={{maxWidth: 860, width: '100%', margin: 'auto'}}>
            <div className='card-header align-items-center'>
              <div>
                <h3 className='card-title mb-0'>{accLabel(account)}</h3>
                <div className='text-muted fs-8 mt-1'>{account.email || account.phone}{accountDdi ? ` · ${accountDdi}` : ''}</div>
              </div>
              <button className='btn btn-sm btn-icon btn-light' onClick={onClose}>✕</button>
            </div>
            <div className='card-body'>
              {loadErr && <div className='alert alert-danger'>{loadErr}</div>}

              <ul className='nav nav-tabs nav-line-tabs mb-4 gap-8'>
                <li className='nav-item'>
                  <a className={`nav-link ${tab === 'signatures' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={() => setTab('signatures')}>
                    חתימות
                  </a>
                </li>
                <li className='nav-item'>
                  <a className={`nav-link ${tab === 'strip' ? 'active' : ''}`} style={{cursor: 'pointer'}} onClick={openStrip}>
                    ניקוי מודעות
                  </a>
                </li>
              </ul>

              {tab === 'signatures' && (
                <>
                  <div className='fs-8 text-muted mb-3'>
                    החתימה תתווסף אוטומטית לכל המודעות בקמפיין. ניתן להשתמש ב־<code dir='ltr'>{'{number}'}</code> וב־<code dir='ltr'>{'{wa_link}'}</code>.
                  </div>
                  {campaigns.length === 0 ? (
                    <div className='text-muted py-6 text-center'>אין קמפיינים.</div>
                  ) : (
                    campaigns.map((c) => <CampaignCard key={c._id} c={c} />)
                  )}

                  <h4 className='mt-6 mb-3'>פרופילים (הורים)</h4>
                  <div className='fs-8 text-muted mb-3'>
                    כל פרופיל הורה יכול לקבל מספר משלו. פרופיל ללא מספר ישתמש ב<b>מספר ברירת המחדל של החשבון</b>. כדי לתת לכל פרופיל מספר אחר — שייכו לחשבון כמה מספרים בטבלת המספרים למטה. תת-פרופילים יורשים מההורה.
                  </div>
                  <div className='border rounded p-3 mb-4 bg-light-warning d-flex align-items-center gap-2 flex-wrap'>
                    <span className='fs-8 fw-bold'>הקצאת מספר לכל פרופילי ההורים:</span>
                    <select className='form-select form-select-sm' style={{width: 210}} value={bulkDid} onChange={(e) => setBulkDid(e.target.value)}>
                      <option value=''>ברירת מחדל (מספר החשבון)</option>
                      {numbers.map((n) => (
                        <option key={n._id} value={n._id}>{n.ddi}</option>
                      ))}
                    </select>
                    <button className='btn btn-sm btn-warning' onClick={assignAll}>הקצה לכולם</button>
                    {bulkMsg && <span className='text-muted fs-8'>{bulkMsg}</span>}
                  </div>
                  {profiles.length === 0 ? (
                    <div className='text-muted py-6 text-center'>אין פרופילים.</div>
                  ) : (
                    profiles.map((p) => <ProfileCard key={p._id} p={p} numbers={numbers} accountId={account._id} />)
                  )}
                </>
              )}

              {tab === 'strip' && (
                <>
                  <div className='d-flex gap-2 align-items-center mb-3 flex-wrap'>
                    <button className='btn btn-sm btn-primary' onClick={suggestAll} disabled={suggesting || postsLoading || !posts.length}>
                      {suggesting ? 'מנקה…' : '🧹 נקה פרטי קשר מכל המודעות'}
                    </button>
                    <button className='btn btn-sm btn-success' onClick={savePosts} disabled={savingPosts || suggesting || !posts.length}>
                      {savingPosts ? 'שומר…' : '💾 שמור הכל'}
                    </button>
                    {stripMsg && <span className='text-muted fs-8'>{stripMsg}</span>}
                  </div>
                  {progress && (
                    <div className='mb-3'>
                      <div className='d-flex justify-content-between fs-8 text-muted mb-1'>
                        <span>מעבד מודעות עם AI…</span>
                        <span dir='ltr'>{progress.done}/{progress.total}</span>
                      </div>
                      <div className='progress' style={{height: 6}}>
                        <div
                          className='progress-bar bg-primary'
                          role='progressbar'
                          style={{width: `${progress.total ? Math.round((progress.done / progress.total) * 100) : 0}%`, transition: 'width .2s'}}
                        />
                      </div>
                    </div>
                  )}
                  <div className='fs-8 text-muted mb-3'>מסיר טלפונים, קישורי וואטסאפ וביטויי "לפרטים" מגוף המודעה. החתימה תתווסף אוטומטית בפרסום.</div>

                  {/* Campaign-global auto-reply — replace per-post replies with one tokened reply */}
                  <div className='border rounded p-3 mb-4' style={{background: 'rgba(37,112,250,0.05)'}}>
                    <div className='fw-bold fs-7 mb-2'>💬 תגובה גלובלית לקמפיין</div>
                    <div className='fs-8 text-muted mb-2'>
                      מוחק את הודעות התגובה שמוגדרות פר-פוסט ומגדיר תגובה אוטומטית אחת לכל הקמפיינים.
                      השתמש ב-<code>{'{number}'}</code> למספר הווירטואלי וב-<code>{'{wa_link}'}</code> לקישור וואטסאפ מתעד.
                    </div>
                    <textarea
                      className='form-control form-control-sm mb-2'
                      rows={3}
                      dir='rtl'
                      value={globalReply}
                      onChange={(e) => setGlobalReply(e.target.value)}
                    />
                    <div className='d-flex gap-2 align-items-center flex-wrap'>
                      <button className='btn btn-sm btn-primary' onClick={consolidateReplies} disabled={consolidating || !globalReply.trim()}>
                        {consolidating ? 'מעדכן…' : '💬 החל תגובה גלובלית ונקה תגובות פר-פוסט'}
                      </button>
                      <button
                        className='btn btn-sm btn-light-warning'
                        onClick={restoreReplies}
                        disabled={restoring || consolidating || !replyBackup?.posts}
                        title={replyBackup?.posts ? 'מחזיר את הודעות התגובה שהיו לפני הניקוי' : 'אין גיבוי תגובות לשחזור בחשבון זה'}
                      >
                        {restoring ? 'משחזר…' : `↩️ שחזר תגובות קודמות${replyBackup?.posts ? ` (${replyBackup.posts})` : ''}`}
                      </button>
                      {replyMsg && <span className='text-muted fs-8'>{replyMsg}</span>}
                    </div>
                    {replyBackup && replyBackup.posts > 0 && (
                      <div className='fs-8 text-muted mt-2'>
                        גיבוי מ-{replyBackup.backed_up_at ? new Date(replyBackup.backed_up_at).toLocaleString('he-IL') : 'תאריך לא ידוע'} ·{' '}
                        {replyBackup.posts} תגובות פר-פוסט
                        {replyBackup.sample ? (
                          <> · לדוגמה: <span dir='rtl' style={{opacity: 0.85}}>"{replyBackup.sample.slice(0, 90)}{replyBackup.sample.length > 90 ? '…' : ''}"</span></>
                        ) : null}
                      </div>
                    )}
                    {replyBackup && replyBackup.posts === 0 && (
                      <div className='fs-8 text-muted mt-2'>
                        אין גיבוי לשחזור — ניקויים שבוצעו לפני הוספת הגיבוי אינם ניתנים לשחזור.
                      </div>
                    )}
                  </div>

                  {/* Bulk phone edit across every post's reply text */}
                  <div className='border rounded p-3 mb-4' style={{background: 'rgba(80,131,230,0.06)'}}>
                    <div className='fw-bold fs-7 mb-2'>📞 עריכת מספר בתגובות (גורף)</div>
                    <div className='fs-8 text-muted mb-2'>
                      מוסיף, מסיר או מחליף מספר טלפון בהודעות התגובה של כל המודעות — בלי AI, חיפוש והחלפה מדויקים.
                      מזהה מספר בכל צורת כתיבה (<code dir='ltr'>052-366-6975</code>, <code dir='ltr'>+972…</code>), קישורי וואטסאפ,
                      וגם את הטוקנים <code>{'{number}'}</code>/<code>{'{wa_link}'}</code> וקישורי מעקב — שכולם ממשיכים להצביע
                      על המספר הווירטואלי עד שמחליפים אותם. תמיד הריצו תצוגה מקדימה לפני החלה.
                    </div>
                    <div className='d-flex gap-3 align-items-center flex-wrap fs-8 mb-2'>
                      <span className='text-muted'>
                        מספר וירטואלי: <code dir='ltr'>{accountDdi || '—'}</code>
                        {'  '}· מספר הלקוח: <code dir='ltr'>{account.phone || '—'}</code>
                      </span>
                      <button className='btn btn-sm btn-light-success py-1' onClick={useRealNumber} disabled={editBusy}>
                        ↩️ החזר למספר האמיתי של הלקוח
                      </button>
                    </div>
                    <div className='d-flex gap-2 align-items-center flex-wrap mb-2'>
                      <select
                        className='form-select form-select-sm'
                        style={{width: 150}}
                        value={editMode}
                        onChange={(e) => {
                          setEditMode(e.target.value as 'remove' | 'replace' | 'add')
                          setEditPreview(null)
                          setEditRows([])
                          setEditMsg('')
                        }}
                      >
                        <option value='replace'>החלף מספר</option>
                        <option value='remove'>הסר מספר</option>
                        <option value='add'>הוסף שורה</option>
                      </select>
                      {editMode !== 'add' && (
                        <input
                          className='form-control form-control-sm'
                          style={{width: 190}}
                          dir='ltr'
                          placeholder='מספר קיים (ריק = כל מספר)'
                          value={editFrom}
                          onChange={(e) => {
                            setEditFrom(e.target.value)
                            setEditPreview(null)
                            setEditRows([])
                          }}
                        />
                      )}
                      {editMode === 'replace' && (
                        <input
                          className='form-control form-control-sm'
                          style={{width: 190}}
                          dir='ltr'
                          placeholder='מספר חדש או {number}'
                          value={editTo}
                          onChange={(e) => {
                            setEditTo(e.target.value)
                            setEditPreview(null)
                            setEditRows([])
                          }}
                        />
                      )}
                      {editMode === 'add' && (
                        <>
                          <input
                            className='form-control form-control-sm'
                            style={{minWidth: 260, flex: 1}}
                            dir='rtl'
                            placeholder='למשל: לפרטים חייגו {number}'
                            value={editText}
                            onChange={(e) => {
                              setEditText(e.target.value)
                              setEditPreview(null)
                            setEditRows([])
                            }}
                          />
                          <select
                            className='form-select form-select-sm'
                            style={{width: 120}}
                            value={editPosition}
                            onChange={(e) => {
                              setEditPosition(e.target.value as 'start' | 'end')
                              setEditPreview(null)
                            setEditRows([])
                            }}
                          >
                            <option value='end'>בסוף</option>
                            <option value='start'>בהתחלה</option>
                          </select>
                        </>
                      )}
                    </div>
                    <label className='d-flex align-items-center gap-2 fs-8 text-muted mb-2' style={{cursor: 'pointer'}}>
                      <input
                        type='checkbox'
                        checked={editDefaults}
                        onChange={(e) => {
                          setEditDefaults(e.target.checked)
                          setEditPreview(null)
                          setEditRows([])
                        }}
                      />
                      לעדכן גם את התגובה הגלובלית של הקמפיינים
                    </label>
                    <div className='d-flex gap-2 align-items-center flex-wrap'>
                      <button
                        className='btn btn-sm btn-light-primary'
                        onClick={() => previewBulkEdit()}
                        disabled={editBusy || (editMode === 'replace' && !editTo.trim()) || (editMode === 'add' && !editText.trim())}
                        title={editMode === 'replace' && !editTo.trim() ? 'יש להזין מספר חדש' : ''}
                      >
                        {editBusy ? 'עובד…' : '🔍 תצוגה מקדימה'}
                      </button>
                      <button
                        className='btn btn-sm btn-primary'
                        onClick={applyBulkEdit}
                        disabled={editBusy || !editRows.some((r) => r.include)}
                        title={editRows.length ? '' : 'הריצו תצוגה מקדימה קודם'}
                      >
                        ✅ החל על הנבחרות ({editRows.filter((r) => r.include).length})
                      </button>
                      {editMsg && <span className='text-muted fs-8'>{editMsg}</span>}
                    </div>
                    {editRows.length > 0 && (
                      <div className='mt-3'>
                        <div className='d-flex align-items-center gap-3 flex-wrap mb-2'>
                          <span className='fs-8 text-muted'>
                            {editRows.length} תגובות לשינוי
                            {editPreview?.truncated ? ` (מוצגות ${editRows.length} מתוך ${(editPreview.changed || 0) + (editPreview.campaigns || 0)} — הריצו שוב אחרי ההחלה כדי להמשיך)` : ''}
                          </span>
                          <button
                            className='btn btn-sm btn-link p-0 fs-8'
                            onClick={() => {
                              const allOn = editRows.every((r) => r.include)
                              setEditRows((prev) => prev.map((r) => ({...r, include: !allOn})))
                            }}
                          >
                            {editRows.every((r) => r.include) ? 'בטל בחירת הכל' : 'בחר הכל'}
                          </button>
                        </div>
                        <div style={{maxHeight: 420, overflowY: 'auto'}}>
                          {editRows.map((row, idx) => (
                            <div className='border rounded p-2 mb-2' key={row.id} style={{background: row.include ? '#fff' : '#f7f7f7'}}>
                              <label className='d-flex align-items-center gap-2 fs-8 text-muted mb-1' style={{cursor: 'pointer'}}>
                                <input
                                  type='checkbox'
                                  checked={row.include}
                                  onChange={(e) => {
                                    const on = e.target.checked
                                    setEditRows((prev) => prev.map((r, i) => (i === idx ? {...r, include: on} : r)))
                                  }}
                                />
                                <span dir='ltr'>{row.post_code || row.id}</span>
                              </label>
                              <div className='fs-8' dir='rtl' style={{whiteSpace: 'pre-wrap', textDecoration: 'line-through', opacity: 0.5}}>{row.before}</div>
                              <textarea
                                className='form-control form-control-sm mt-1'
                                dir='rtl'
                                rows={Math.min(6, Math.max(2, row.after.split('\n').length))}
                                value={row.after}
                                disabled={!row.include}
                                onChange={(e) => {
                                  const v = e.target.value
                                  setEditRows((prev) => prev.map((r, i) => (i === idx ? {...r, after: v} : r)))
                                }}
                              />
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Reverse flow — put the client's real number back into the posts */}
                  <div className='border rounded p-3 mb-4' style={{background: 'rgba(255,199,0,0.08)'}}>
                    <div className='fw-bold fs-7 mb-2'>↩️ החזרת מספר אמיתי למודעות</div>
                    <div className='fs-8 text-muted mb-2'>
                      ה-AI יחליף בכל המודעות את המספר הווירטואלי ואת קישור המעקב בקישור וואטסאפ רגיל למספר שתזינו
                      {plainWaLink ? <> (<code dir='ltr'>{plainWaLink}</code>)</> : null}. לאחר הבדיקה — שמרו והסירו את המספר הווירטואלי.
                    </div>
                    <div className='d-flex gap-2 align-items-center flex-wrap'>
                      <input
                        className='form-control form-control-sm'
                        style={{width: 190}}
                        dir='ltr'
                        placeholder='05xxxxxxxx'
                        value={reversePhone}
                        onChange={(e) => setReversePhone(e.target.value)}
                      />
                      <button
                        className='btn btn-sm btn-warning'
                        onClick={suggestAddAll}
                        disabled={reversing || suggesting || postsLoading || !posts.length || !reversePhone.trim()}
                      >
                        {reversing ? 'מוסיף…' : '📞 החזר את המספר לכל המודעות'}
                      </button>
                      <button
                        className='btn btn-sm btn-danger'
                        onClick={saveReverseAndRemove}
                        disabled={removing || reversing || suggesting || !posts.length}
                      >
                        {removing ? 'שומר ומסיר…' : '💾 שמור והסר מספר וירטואלי'}
                      </button>
                    </div>
                  </div>

                  {postsLoading ? (
                    <div className='text-muted py-6 text-center'>טוען מודעות…</div>
                  ) : posts.length === 0 ? (
                    <div className='text-muted py-6 text-center'>אין מודעות.</div>
                  ) : (
                    posts.map((p) => {
                      // In the reverse flow the signature is about to be cleared, so the
                      // preview must show the body alone — not body + soon-dead signature.
                      const campSig = reversePhone.trim() ? '' : (campaigns.find((c) => c._id === p.category)?.default_signature || '').trim()
                      const body = (p.suggested || p.post_content || '').trim()
                      const filledSig = campSig.replace(/\{number\}/g, accountDdi || '{number}').replace(/\{wa_link\}/g, '🔗 קישור וואטסאפ')
                      const preview = campSig ? (body ? `${body}\n${filledSig}` : filledSig) : body
                      return (
                        <div className='border rounded p-4 mb-3' key={p.id}>
                          <div className='d-flex justify-content-between align-items-center mb-2'>
                            <label className='d-flex align-items-center gap-2 m-0' style={{cursor: 'pointer'}}>
                              <input type='checkbox' checked={p.include} onChange={(e) => setPosts((prev) => prev.map((x) => (x.id === p.id ? {...x, include: e.target.checked} : x)))} style={{width: 16, height: 16, margin: 0}} />
                              <span className='fw-bold'>{p.name || 'מודעה'}</span>
                            </label>
                            {p.post_code && <span className='badge badge-light-success' dir='ltr'>{p.post_code}</span>}
                          </div>
                          <div className='row g-3'>
                            <div className='col-md-6'>
                              <div className='text-muted fs-8 mb-1'>מקורי</div>
                              <div className='text-muted fs-8 p-2 bg-light rounded' style={{whiteSpace: 'pre-wrap', maxHeight: 160, overflow: 'auto'}}>{p.post_content || '—'}</div>
                            </div>
                            <div className='col-md-6'>
                              <div className='text-muted fs-8 mb-1'>לאחר ניקוי (ניתן לעריכה)</div>
                              <textarea className='form-control form-control-sm' rows={7} placeholder='לחץ "נקה פרטי קשר"' value={p.suggested} onChange={(e) => setPosts((prev) => prev.map((x) => (x.id === p.id ? {...x, suggested: e.target.value} : x)))} />
                            </div>
                          </div>
                          <div className='mt-3'>
                            <div className='text-muted fs-8 mb-1'>תצוגה מקדימה — כפי שיתפרסם (כולל חתימה)</div>
                            <div className='fs-8 p-2 rounded' style={{whiteSpace: 'pre-wrap', maxHeight: 200, overflow: 'auto', background: 'rgba(80,131,230,0.08)', border: '1px solid rgba(80,131,230,0.25)'}} dir='rtl'>{preview || '—'}</div>
                          </div>
                        </div>
                      )
                    })
                  )}
                </>
              )}
            </div>
          </div>
        </div>
  )
}
